import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Package, ArrowLeft, Monitor, Smartphone, ShieldAlert, RefreshCw, AlertTriangle, Trash2, Download, Play, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(ts); }
}
function ago(ts) {
  if (!ts) return 'nunca';
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (s < 3600) return `hace ${Math.round(s / 60)} min`;
  if (s < 86400) return `hace ${Math.round(s / 3600)} h`;
  return `hace ${Math.round(s / 86400)} d`;
}

// Estado de seguridad de un software a partir del risk_rank (0-4) + is_eol
function statusOf(r) {
  if (r.risk_rank >= 3) return { key: 'alto', label: r.risk_rank === 4 ? 'Crítico' : 'Alto', color: '#e24b4a', bg: 'rgba(226,75,74,0.15)' };
  if (r.risk_rank >= 1) return { key: 'medio', label: r.risk_rank === 2 ? 'Medio' : 'Bajo', color: '#ef9f27', bg: 'rgba(239,159,39,0.15)' };
  if (r.is_eol)         return { key: 'eol',   label: 'Obsoleto', color: '#ef9f27', bg: 'rgba(239,159,39,0.15)' };
  return { key: 'ok', label: 'OK', color: 'var(--green)', bg: 'rgba(34,197,94,0.13)' };
}
const CAT_LABEL = { p2p: 'P2P', piracy: 'Pirateado', browser: 'Navegador', eol: 'Obsoleto/EOL', remote: 'Acceso remoto', vpn: 'VPN', other: 'Otro' };

export default function Software() {
  const [data, setData] = useState({ rows: [], last_analyzed: null });
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [q, setQ] = useState('');
  const [groupBy, setGroupBy] = useState('estado');
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [source, setSource] = useState('');
  const [selected, setSelected] = useState(null);
  const [equipos, setEquipos] = useState(null);
  const [loadingEq, setLoadingEq] = useState(false);
  const [remediating, setRemediating] = useState(false);   // acción en curso ('uninstall'|'update')
  const [remedResult, setRemedResult] = useState(null);     // { script_id, script_name, action, count, assets }
  const [running, setRunning] = useState(false);
  const [runInfo, setRunInfo] = useState(null);             // { run_id } o { error }

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const isAdmin = user.role === 'admin';

  // ── Remediación: genera el script pre-apuntado y abre el modal de confirmación
  const remediate = async (action) => {
    if (!selected) return;
    setRemediating(action); setRunInfo(null);
    try {
      const r = await fetch(`${API_URL}/software/remediate`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: selected, action }),
      });
      const d = await r.json();
      if (!r.ok) { setRemedResult({ error: d.error || 'Error generando el script.' }); }
      else { setRemedResult(d); }
    } catch { setRemedResult({ error: 'Error de red generando el script.' }); }
    finally { setRemediating(false); }
  };

  // ── Ejecuta el script recién generado en los equipos afectados
  const runRemediation = async () => {
    if (!remedResult?.script_id) return;
    setRunning(true);
    try {
      const ids = (remedResult.assets || []).map(a => a.id);
      const r = await fetch(`${API_URL}/automation/run`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ script_id: remedResult.script_id, target: { type: 'asset', ids } }),
      });
      const d = await r.json();
      if (!r.ok) setRunInfo({ error: d.error || 'Error ejecutando.' });
      else setRunInfo({ run_id: d.run_id, total: d.total_targets });
    } catch { setRunInfo({ error: 'Error de red al ejecutar.' }); }
    finally { setRunning(false); }
  };
  const closeRemed = () => { setRemedResult(null); setRunInfo(null); };

  const loadCatalog = useCallback(() => {
    setLoading(true);
    const qs = source ? `?source=${source}` : '';
    fetch(`${API_URL}/software/catalog${qs}`, { headers })
      .then(r => r.json())
      .then(d => setData({ rows: Array.isArray(d.rows) ? d.rows : [], last_analyzed: d.last_analyzed }))
      .catch(() => setData({ rows: [], last_analyzed: null }))
      .finally(() => setLoading(false));
  }, [source]); // eslint-disable-line

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const analyze = async () => {
    setAnalyzing(true);
    try { await fetch(`${API_URL}/hygiene/analyze`, { method: 'POST', headers }); loadCatalog(); }
    catch { /* noop */ }
    finally { setAnalyzing(false); }
  };

  const openSoftware = useCallback(async (name) => {
    setSelected(name); setEquipos(null); setLoadingEq(true); setRemedResult(null); setRunInfo(null);
    try {
      const r = await fetch(`${API_URL}/software/search?q=${encodeURIComponent(name)}&exact=1`, { headers });
      const d = await r.json();
      setEquipos(d.rows || []);
    } catch { setEquipos([]); }
    finally { setLoadingEq(false); }
  }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    let rows = data.rows;
    const s = q.trim().toLowerCase();
    if (s) rows = rows.filter(c => c.name.toLowerCase().includes(s));
    if (onlyProblems) rows = rows.filter(c => c.risk_rank > 0 || c.is_eol);
    return rows;
  }, [q, data.rows, onlyProblems]);

  const summary = useMemo(() => {
    let risk = 0, eol = 0, ok = 0;
    for (const r of data.rows) {
      if (r.risk_rank > 0) risk++;
      else if (r.is_eol) eol++;
      else ok++;
    }
    return { risk, eol, ok };
  }, [data.rows]);

  // Agrupación
  const groups = useMemo(() => {
    if (groupBy === 'none') return [['', filtered]];
    const m = new Map();
    for (const r of filtered) {
      let key;
      if (groupBy === 'estado') key = statusOf(r).key === 'ok' ? 'OK' : statusOf(r).key === 'eol' ? 'Obsoleto (requiere actualización)' : statusOf(r).label === 'Crítico' || statusOf(r).label === 'Alto' ? 'Riesgo alto' : 'Riesgo medio/bajo';
      else if (groupBy === 'categoria') key = r.category ? (CAT_LABEL[r.category] || r.category) : 'Sin categoría';
      else key = r.publisher || 'Sin publisher';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    const order = { 'Riesgo alto': 0, 'Riesgo medio/bajo': 1, 'Obsoleto (requiere actualización)': 2, 'OK': 3 };
    return [...m.entries()].sort((a, b) => (order[a[0]] ?? 9) - (order[b[0]] ?? 9) || b[1].length - a[1].length);
  }, [filtered, groupBy]);

  const th = { textAlign: 'left', padding: '9px 14px', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const td = { padding: '9px 14px', fontSize: 13, color: 'var(--ink)', borderBottom: '1px solid var(--border)' };
  const badge = (bg, color, txt) => <span style={{ fontSize: 11, fontWeight: 600, color, whiteSpace: 'nowrap' }}>{txt}</span>;

  const RowBadges = ({ r }) => {
    const st = statusOf(r);
    return (
      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
        {st.key !== 'ok' ? badge(st.bg, st.color, st.key === 'eol' ? 'Obsoleto' : `Riesgo ${st.label.toLowerCase()}`) : badge(st.bg, st.color, 'OK')}
        {r.is_eol && st.key !== 'eol' && badge('rgba(239,159,39,0.15)', '#ef9f27', 'EOL')}
        {r.category && badge('var(--bg-subtle)', 'var(--ink-2)', CAT_LABEL[r.category] || r.category)}
      </span>
    );
  };

  const catalogTable = (rows) => (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Software</th>
              <th style={th}>Estado</th>
              <th style={{ ...th, textAlign: 'right' }}>Equipos</th>
              <th style={{ ...th, textAlign: 'right' }}>Versiones</th>
              <th style={th}>Último visto</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.name} onClick={() => openSoftware(c.name)} style={{ cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ ...td, fontWeight: 500 }}>{c.name}</td>
                <td style={td}><RowBadges r={c} /></td>
                <td style={{ ...td, textAlign: 'right' }}><span style={{ fontWeight: 600, color: 'var(--accent)' }}>{c.asset_count}</span></td>
                <td style={{ ...td, textAlign: 'right', color: 'var(--ink-2)' }}>{c.version_count}</td>
                <td style={{ ...td, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{fmt(c.last_seen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const selStyle = { padding: '8px 10px', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13, background: 'var(--bg-subtle)', color: 'var(--ink)' };

  return (
    <div className="page">
      <div style={{ marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Package size={20} /> Software instalado
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-2)' }}>
          Inventario de software con estado de seguridad. Clic en un programa para ver los equipos.
        </p>
      </div>

      {!selected && (
        <>
          {/* Resumen */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '16px 0' }}>
            {[
              ['Con riesgo', summary.risk, '#e24b4a'],
              ['Obsoletos', summary.eol, '#ef9f27'],
              ['Sin problemas', summary.ok, 'var(--green)'],
            ].map(([l, v, c]) => (
              <div key={l} style={{ flex: 1, minWidth: 130, background: 'var(--bg-subtle)', borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ fontSize: 24, fontWeight: 600, color: c }}>{v}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>{l}</div>
              </div>
            ))}
          </div>

          {/* Controles */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
              <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-2)' }} />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar software…"
                style={{ ...selStyle, width: '100%', paddingLeft: 36 }} />
            </div>
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)} style={selStyle} title="Agrupar por">
              <option value="estado">Agrupar: Estado</option>
              <option value="categoria">Agrupar: Categoría</option>
              <option value="publisher">Agrupar: Publisher</option>
              <option value="none">Sin agrupar</option>
            </select>
            <select value={source} onChange={e => setSource(e.target.value)} style={selStyle} title="Filtrar por origen del install">
              <option value="">Origen: todos</option>
              <option value="user">Origen: instalado por usuario</option>
              <option value="machine">Origen: instalado por máquina</option>
              <option value="process">Origen: portátil (por proceso)</option>
            </select>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink)', cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyProblems} onChange={e => setOnlyProblems(e.target.checked)} /> Solo con problemas
            </label>
            <button onClick={analyze} disabled={analyzing}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--accent)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: analyzing ? 0.6 : 1 }}>
              <RefreshCw size={14} /> {analyzing ? 'Analizando…' : 'Analizar ahora'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12 }}>
            Estado de seguridad analizado {ago(data.last_analyzed)}{data.last_analyzed ? ` (${fmt(data.last_analyzed)})` : ' — corré "Analizar ahora"'}.
          </div>

          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--ink-2)' }}>Cargando…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--ink-2)' }}>Sin resultados.</div>
          ) : groups.map(([g, rows]) => (
            <div key={g || 'all'}>
              {g && <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', margin: '6px 2px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                {(g.startsWith('Riesgo') || g.startsWith('Obsoleto')) && <AlertTriangle size={14} style={{ color: g.startsWith('Riesgo alto') ? '#e24b4a' : '#ef9f27' }} />}
                {g} <span style={{ color: 'var(--ink-2)', fontWeight: 400 }}>({rows.length})</span>
              </div>}
              {catalogTable(rows)}
            </div>
          ))}
          {!loading && <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>{filtered.length} programas</div>}
        </>
      )}

      {selected && (
        <div style={{ marginTop: 18 }}>
          <button onClick={() => { setSelected(null); setEquipos(null); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--bg-subtle)', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: 'var(--ink)', marginBottom: 14 }}>
            <ArrowLeft size={14} /> Volver al catálogo
          </button>
          <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 600 }}>{selected}</h2>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--ink-2)' }}>
            {loadingEq ? 'Buscando…' : `Instalado en ${equipos?.length || 0} equipo${(equipos?.length || 0) === 1 ? '' : 's'}`}
          </p>

          {/* Panel de remediación */}
          {isAdmin && (() => {
            const winCount = (equipos || []).filter(e => (e.os_name || '').toLowerCase().includes('windows')).length;
            return (
              <div className="card" style={{ padding: '14px 16px', marginBottom: 16, borderLeft: '3px solid var(--accent)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>Remediación</div>
                <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 10 }}>
                  Generá un script de Automatización pre-apuntado a los equipos Windows con este software. Se pedirá confirmación antes de ejecutar.
                  {winCount === 0 && ' (No hay equipos Windows con este software.)'}
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button onClick={() => remediate('uninstall')} disabled={!!remediating || winCount === 0}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(226,75,74,0.12)', color: '#e24b4a', border: '1px solid rgba(226,75,74,0.4)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: winCount === 0 ? 'not-allowed' : 'pointer', opacity: (remediating || winCount === 0) ? 0.6 : 1 }}>
                    <Trash2 size={14} /> {remediating === 'uninstall' ? 'Generando…' : 'Desinstalar en los equipos'}
                  </button>
                  <button onClick={() => remediate('update')} disabled={!!remediating || winCount === 0}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--bg-subtle)', color: 'var(--ink)', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: winCount === 0 ? 'not-allowed' : 'pointer', opacity: (remediating || winCount === 0) ? 0.6 : 1 }}>
                    <Download size={14} /> {remediating === 'update' ? 'Generando…' : 'Actualizar en los equipos'}
                  </button>
                </div>
              </div>
            );
          })()}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Equipo</th>
                    <th style={th}>Sistema</th>
                    <th style={th}>Versión</th>
                    <th style={th}>Publisher</th>
                    <th style={th}>Último visto</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingEq ? (
                    <tr><td style={{ ...td, textAlign: 'center', color: 'var(--ink-2)', padding: 30 }} colSpan={5}>Cargando…</td></tr>
                  ) : (equipos || []).length === 0 ? (
                    <tr><td style={{ ...td, textAlign: 'center', color: 'var(--ink-2)', padding: 30 }} colSpan={5}>Sin equipos.</td></tr>
                  ) : equipos.map(e => (
                    <tr key={e.asset_id + e.version}>
                      <td style={td}>
                        <Link to={`/assets/${e.asset_id}`} style={{ color: 'var(--accent)', fontWeight: 500, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {e.agent_type === 'android' ? <Smartphone size={13} /> : <Monitor size={13} />} {e.hostname}
                        </Link>
                      </td>
                      <td style={{ ...td, color: 'var(--ink-2)' }}>{e.os_name || '—'}</td>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{e.version || '—'}</td>
                      <td style={{ ...td, color: 'var(--ink-2)' }}>{e.publisher || '—'}</td>
                      <td style={{ ...td, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{fmt(e.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal de remediación */}
      {remedResult && (
        <div onClick={closeRemed} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} className="card" style={{ maxWidth: 520, width: '100%', padding: 22, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>
                {remedResult.error ? 'No se pudo generar' : runInfo?.run_id ? 'Remediación en ejecución' : 'Confirmar remediación'}
              </h3>
              <button onClick={closeRemed} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', padding: 2 }}><X size={18} /></button>
            </div>

            {remedResult.error ? (
              <p style={{ fontSize: 13, color: '#e24b4a', margin: 0 }}>{remedResult.error}</p>
            ) : runInfo?.error ? (
              <p style={{ fontSize: 13, color: '#e24b4a', margin: 0 }}>{runInfo.error}</p>
            ) : runInfo?.run_id ? (
              <div>
                <p style={{ fontSize: 13, color: 'var(--ink)', margin: '0 0 14px' }}>
                  Se ejecutó <b>{remedResult.script_name}</b> en <b>{runInfo.total}</b> equipo(s). Podés seguir el avance en Automatización.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <Link to={`/automation?run=${runInfo.run_id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--surface)', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                    Ver la corrida
                  </Link>
                  <button onClick={closeRemed} style={{ padding: '8px 14px', background: 'var(--bg-subtle)', color: 'var(--ink)', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cerrar</button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: 13, color: 'var(--ink)', margin: '0 0 6px' }}>
                  {remedResult.reused ? 'Se reutilizó el script ' : 'Se generó el script '}
                  <b>{remedResult.script_name}</b> en Automatización (carpeta "Remediación de software").
                </p>
                <p style={{ fontSize: 13, color: 'var(--ink)', margin: '0 0 10px' }}>
                  Va a {remedResult.action === 'uninstall' ? <b style={{ color: '#e24b4a' }}>desinstalar</b> : <b>actualizar</b>} <b>{selected}</b> en <b>{remedResult.count}</b> equipo(s) Windows:
                </p>
                <div style={{ maxHeight: 150, overflowY: 'auto', background: 'var(--bg-subtle)', borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
                  {(remedResult.assets || []).map(a => (
                    <div key={a.id} style={{ fontSize: 12, color: 'var(--ink-2)', padding: '2px 0' }}>{a.hostname}</div>
                  ))}
                </div>
                {remedResult.action === 'uninstall' && (
                  <div style={{ fontSize: 12, color: '#e24b4a', background: 'rgba(226,75,74,0.1)', borderRadius: 6, padding: '8px 10px', marginBottom: 14 }}>
                    <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
                    Acción destructiva: se ejecuta como SYSTEM en los equipos. Verificá la lista antes de continuar.
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button onClick={runRemediation} disabled={running}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: remedResult.action === 'uninstall' ? '#e24b4a' : 'var(--accent)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: running ? 0.6 : 1 }}>
                    <Play size={14} /> {running ? 'Ejecutando…' : `Ejecutar en ${remedResult.count} equipo(s)`}
                  </button>
                  <Link to={`/automation?script=${remedResult.script_id}`} style={{ padding: '9px 16px', background: 'var(--bg-subtle)', color: 'var(--ink)', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13, textDecoration: 'none' }}>
                    Ver en Automatización
                  </Link>
                  <button onClick={closeRemed} style={{ padding: '9px 16px', background: 'none', color: 'var(--ink-2)', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
